import React, { useEffect, useState, useCallback } from 'react';
import AppInsightsService from '../services/AppInsightsService';
import {
  AppInsightsLogEntry,
  AppInsightsLogType,
  AppInsightsQueryRequest,
  AppInsightsQueryResponse,
} from '../types/AppInsightsTypes';
import './AppInsights.css';

const LOG_TYPES: { value: AppInsightsLogType; label: string }[] = [
  { value: 'exceptions', label: 'Exceptions' },
  { value: 'requests',   label: 'Requests' },
  { value: 'traces',     label: 'Traces' },
];

const AppInsights: React.FC = () => {
  const [entries, setEntries]           = useState<AppInsightsLogEntry[]>([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [validationMsg, setValidationMsg] = useState<string | null>(null);

  const [customers, setCustomers]       = useState<string[]>([]);
  const [environments, setEnvironments] = useState<string[]>([]);

  const [filters, setFilters] = useState<AppInsightsQueryRequest>({
    logType: 'exceptions',
    page: 1,
    pageSize: 50,
    sortDirection: 'desc',
  });

  const [totalCount, setTotalCount]   = useState(0);
  const [totalPages, setTotalPages]   = useState(0);

  // Track whether we've ever triggered a search (so the grid doesn't show on first load)
  const [hasSearched, setHasSearched] = useState(false);

  const [selectedEntry, setSelectedEntry] = useState<AppInsightsLogEntry | null>(null);

  // ── Load customer list once on mount ────────────────────────────────────────
  useEffect(() => {
    AppInsightsService.getCustomers()
      .then(setCustomers)
      .catch(() => setCustomers([]));
  }, []);

  // ── Reload environments whenever customer changes ─────────────────────────
  useEffect(() => {
    if (!filters.customer) {
      setEnvironments([]);
      return;
    }
    AppInsightsService.getEnvironments(filters.customer)
      .then(setEnvironments)
      .catch(() => setEnvironments([]));
  }, [filters.customer]);

  // ── Close modal on ESC ────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEntry(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Re-query when page or sort direction changes (if already searched) ────
  useEffect(() => {
    if (hasSearched) {
      runQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.sortDirection, filters.logType]);

  // ── Core query function ──────────────────────────────────────────────────
  const runQuery = useCallback(async () => {
    if (!filters.customer || !filters.environment) {
      setValidationMsg('Please select a customer and environment.');
      return;
    }
    setValidationMsg(null);
    setLoading(true);
    setError(null);

    try {
      const result: AppInsightsQueryResponse = await AppInsightsService.queryAppInsights(filters);
      setEntries(result.entries || []);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load App Insights data');
      setEntries([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const handleFilterChange = (field: keyof AppInsightsQueryRequest, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handleCustomerChange = (customer: string) => {
    setFilters(prev => ({
      ...prev,
      customer: customer || undefined,
      environment: undefined,
      page: 1,
    }));
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
    runQuery();
  };

  const handleReset = () => {
    setFilters({ logType: 'exceptions', page: 1, pageSize: 50, sortDirection: 'desc' });
    setEntries([]);
    setTotalCount(0);
    setTotalPages(0);
    setHasSearched(false);
    setValidationMsg(null);
    setError(null);
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // ── Date helpers ─────────────────────────────────────────────────────────
  const formatDateTimeLocal = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const handleQuickDateRange = (range: 'lastHour' | 'last4Hours' | 'today' | 'yesterday' | 'thisWeek' | 'last7Days') => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    switch (range) {
      case 'lastHour':    startDate = new Date(now.getTime() - 60 * 60 * 1000); break;
      case 'last4Hours':  startDate = new Date(now.getTime() - 4 * 60 * 60 * 1000); break;
      case 'today':       startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate   = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'thisWeek': {
        const diff = now.getDay() === 0 ? 6 : now.getDay() - 1;
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
        break;
      }
      case 'last7Days':   startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
      default: return;
    }

    setFilters(prev => ({
      ...prev,
      startDate: formatDateTimeLocal(startDate),
      endDate: formatDateTimeLocal(endDate),
      page: 1,
    }));
  };

  // ── Formatting helpers ────────────────────────────────────────────────────
  const formatTimestamp = (ts: string) => {
    if (!ts) return 'N/A';
    try {
      const d = new Date(ts);
      const pad = (n: number) => String(n).padStart(2, '0');
      const ms  = String(d.getMilliseconds()).padStart(3, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${ms}`;
    } catch { return 'Invalid'; }
  };

  const getSeverityClass = (severity: string) => {
    const s = (severity || '').toLowerCase();
    if (s === 'error' || s === 'critical') return 'severity-error';
    if (s === 'warning')                   return 'severity-warning';
    if (s === 'information')               return 'severity-information';
    if (s === 'verbose')                   return 'severity-verbose';
    return 'severity-default';
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const renderTableHead = () => {
    switch (filters.logType) {
      case 'requests':
        return (
          <tr>
            <th>Timestamp</th>
            <th>Name</th>
            <th>Duration</th>
            <th>Status Code</th>
            <th>Success</th>
          </tr>
        );
      case 'traces':
        return (
          <tr>
            <th>Timestamp</th>
            <th>Severity</th>
            <th>Message</th>
          </tr>
        );
      default: // exceptions
        return (
          <tr>
            <th>Timestamp</th>
            <th>Severity</th>
            <th>Exception Type</th>
            <th>Message</th>
          </tr>
        );
    }
  };

  const renderTableRow = (entry: AppInsightsLogEntry, idx: number) => {
    switch (filters.logType) {
      case 'requests':
        return (
          <tr key={idx} onClick={() => setSelectedEntry(entry)}>
            <td className="log-timestamp">{formatTimestamp(entry.timestamp)}</td>
            <td>{entry.name || 'N/A'}</td>
            <td>{entry.duration || 'N/A'}</td>
            <td>{entry.statusCode || 'N/A'}</td>
            <td>
              {entry.success === undefined
                ? 'N/A'
                : <span className={entry.success ? 'success-true' : 'success-false'}>
                    {entry.success ? 'Yes' : 'No'}
                  </span>
              }
            </td>
          </tr>
        );
      case 'traces':
        return (
          <tr key={idx} onClick={() => setSelectedEntry(entry)}>
            <td className="log-timestamp">{formatTimestamp(entry.timestamp)}</td>
            <td>
              <span className={`severity-badge ${getSeverityClass(entry.severity)}`}>
                {entry.severity || 'N/A'}
              </span>
            </td>
            <td className="log-message">{entry.message || 'N/A'}</td>
          </tr>
        );
      default: // exceptions
        return (
          <tr key={idx} onClick={() => setSelectedEntry(entry)}>
            <td className="log-timestamp">{formatTimestamp(entry.timestamp)}</td>
            <td>
              <span className={`severity-badge ${getSeverityClass(entry.severity)}`}>
                {entry.severity || 'N/A'}
              </span>
            </td>
            <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{entry.exceptionType || 'N/A'}</td>
            <td className="log-message">{entry.message || 'N/A'}</td>
          </tr>
        );
    }
  };

  const colCount = filters.logType === 'exceptions' ? 4 : filters.logType === 'requests' ? 5 : 3;

  return (
    <div className="app-insights">
      <h1>App Insights</h1>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label>Customer</label>
            <select
              value={filters.customer || ''}
              onChange={e => handleCustomerChange(e.target.value)}
            >
              <option value="">Select Customer</option>
              {customers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label>Environment</label>
            <select
              value={filters.environment || ''}
              onChange={e => handleFilterChange('environment', e.target.value || undefined)}
              disabled={!filters.customer}
            >
              <option value="">Select Environment</option>
              {environments.map(env => <option key={env} value={env}>{env}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label>Log Type</label>
            <select
              value={filters.logType}
              onChange={e => handleFilterChange('logType', e.target.value as AppInsightsLogType)}
            >
              {LOG_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label>Page Size</label>
            <select
              value={filters.pageSize}
              onChange={e => handleFilterChange('pageSize', parseInt(e.target.value))}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={filters.startDate || ''}
              onChange={e => handleFilterChange('startDate', e.target.value || undefined)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={filters.endDate || ''}
              onChange={e => handleFilterChange('endDate', e.target.value || undefined)}
            />
          </div>

          <div className="filter-group">
            <label>Search Text</label>
            <input
              type="text"
              placeholder="Search in messages..."
              value={filters.searchText || ''}
              onChange={e => handleFilterChange('searchText', e.target.value || undefined)}
            />
          </div>

          <div className="filter-group">
            <label>Sort</label>
            <select
              value={filters.sortDirection || 'desc'}
              onChange={e => handleFilterChange('sortDirection', e.target.value as 'asc' | 'desc')}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
        </div>

        <div className="date-range-buttons">
          <label>Quick Date Ranges:</label>
          <div className="button-group">
            <button onClick={() => handleQuickDateRange('lastHour')}    className="btn-date-range">Last Hour</button>
            <button onClick={() => handleQuickDateRange('last4Hours')}  className="btn-date-range">Last 4 Hours</button>
            <button onClick={() => handleQuickDateRange('today')}       className="btn-date-range">Today</button>
            <button onClick={() => handleQuickDateRange('yesterday')}   className="btn-date-range">Yesterday</button>
            <button onClick={() => handleQuickDateRange('thisWeek')}    className="btn-date-range">This Week</button>
            <button onClick={() => handleQuickDateRange('last7Days')}   className="btn-date-range">Last 7 Days</button>
          </div>
        </div>

        <div className="filter-actions">
          {validationMsg && <span className="validation-message">{validationMsg}</span>}
          <button onClick={handleSearch} className="btn-search">Search</button>
          <button onClick={handleReset}  className="btn-reset">Reset</button>
        </div>
      </div>

      {loading && <div className="loading">Loading App Insights data...</div>}

      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={runQuery}>Retry</button>
        </div>
      )}

      {!loading && !error && hasSearched && (
        <>
          <div className="results-info">
            <p>
              Showing {entries.length} of {totalCount.toLocaleString()} results
              {totalPages > 0 && ` (Page ${filters.page} of ${totalPages})`}
            </p>
          </div>

          <div className="log-table-container">
            <table className="log-table">
              <thead>{renderTableHead()}</thead>
              <tbody>
                {entries.length === 0
                  ? <tr><td colSpan={colCount} className="no-data">No results found</td></tr>
                  : entries.map((entry, idx) => renderTableRow(entry, idx))
                }
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange((filters.page || 1) - 1)}
                disabled={(filters.page || 1) <= 1}
                className="btn-page"
              >
                Previous
              </button>
              <span className="page-info">Page {filters.page || 1} of {totalPages}</span>
              <button
                onClick={() => handlePageChange((filters.page || 1) + 1)}
                disabled={(filters.page || 1) >= totalPages}
                className="btn-page"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedEntry && (
        <div className="modal-overlay" onClick={() => setSelectedEntry(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Log Entry Detail</h2>
              <button className="modal-close" onClick={() => setSelectedEntry(null)}>&times;</button>
            </div>
            <div className="modal-body">

              <div className="modal-section">
                <div className="modal-section-title">Summary</div>
                <div className="modal-fields">
                  <span className="modal-field-label">Timestamp</span>
                  <span className="modal-field-value">{formatTimestamp(selectedEntry.timestamp)}</span>
                  {selectedEntry.severity && (
                    <>
                      <span className="modal-field-label">Severity</span>
                      <span className="modal-field-value">
                        <span className={`severity-badge ${getSeverityClass(selectedEntry.severity)}`}>{selectedEntry.severity}</span>
                      </span>
                    </>
                  )}
                  {selectedEntry.exceptionType && (
                    <>
                      <span className="modal-field-label">Exception Type</span>
                      <span className="modal-field-value">{selectedEntry.exceptionType}</span>
                    </>
                  )}
                  {selectedEntry.name && (
                    <>
                      <span className="modal-field-label">Name</span>
                      <span className="modal-field-value">{selectedEntry.name}</span>
                    </>
                  )}
                  {selectedEntry.duration && (
                    <>
                      <span className="modal-field-label">Duration</span>
                      <span className="modal-field-value">{selectedEntry.duration}</span>
                    </>
                  )}
                  {selectedEntry.statusCode && (
                    <>
                      <span className="modal-field-label">Status Code</span>
                      <span className="modal-field-value">{selectedEntry.statusCode}</span>
                    </>
                  )}
                  {selectedEntry.success !== null && selectedEntry.success !== undefined && (
                    <>
                      <span className="modal-field-label">Success</span>
                      <span className={`modal-field-value ${selectedEntry.success ? 'success-true' : 'success-false'}`}>
                        {selectedEntry.success ? 'Yes' : 'No'}
                      </span>
                    </>
                  )}
                  {selectedEntry.message && (
                    <>
                      <span className="modal-field-label">Message</span>
                      <span className="modal-field-value wrap">{selectedEntry.message}</span>
                    </>
                  )}
                </div>
              </div>

              {(selectedEntry.innermostType || selectedEntry.innermostMessage || selectedEntry.problemId ||
                selectedEntry.handledAt || selectedEntry.assembly || selectedEntry.method) && (
                <div className="modal-section">
                  <div className="modal-section-title">Exception Detail</div>
                  <div className="modal-fields">
                    {selectedEntry.innermostType && (
                      <>
                        <span className="modal-field-label">Innermost Type</span>
                        <span className="modal-field-value">{selectedEntry.innermostType}</span>
                      </>
                    )}
                    {selectedEntry.innermostMessage && (
                      <>
                        <span className="modal-field-label">Innermost Message</span>
                        <span className="modal-field-value wrap">{selectedEntry.innermostMessage}</span>
                      </>
                    )}
                    {selectedEntry.problemId && (
                      <>
                        <span className="modal-field-label">Problem ID</span>
                        <span className="modal-field-value">{selectedEntry.problemId}</span>
                      </>
                    )}
                    {selectedEntry.handledAt && (
                      <>
                        <span className="modal-field-label">Handled At</span>
                        <span className="modal-field-value">{selectedEntry.handledAt}</span>
                      </>
                    )}
                    {selectedEntry.assembly && (
                      <>
                        <span className="modal-field-label">Assembly</span>
                        <span className="modal-field-value">{selectedEntry.assembly}</span>
                      </>
                    )}
                    {selectedEntry.method && (
                      <>
                        <span className="modal-field-label">Method</span>
                        <span className="modal-field-value">{selectedEntry.method}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {(selectedEntry.url || selectedEntry.performanceBucket || selectedEntry.source || selectedEntry.applicationVersion) && (
                <div className="modal-section">
                  <div className="modal-section-title">Request Detail</div>
                  <div className="modal-fields">
                    {selectedEntry.url && (
                      <>
                        <span className="modal-field-label">URL</span>
                        <span className="modal-field-value wrap">{selectedEntry.url}</span>
                      </>
                    )}
                    {selectedEntry.performanceBucket && (
                      <>
                        <span className="modal-field-label">Performance Bucket</span>
                        <span className="modal-field-value">{selectedEntry.performanceBucket}</span>
                      </>
                    )}
                    {selectedEntry.source && (
                      <>
                        <span className="modal-field-label">Source</span>
                        <span className="modal-field-value">{selectedEntry.source}</span>
                      </>
                    )}
                    {selectedEntry.applicationVersion && (
                      <>
                        <span className="modal-field-label">App Version</span>
                        <span className="modal-field-value">{selectedEntry.applicationVersion}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {(selectedEntry.operationId || selectedEntry.operationName || selectedEntry.operationParentId) && (
                <div className="modal-section">
                  <div className="modal-section-title">Correlation</div>
                  <div className="modal-fields">
                    {selectedEntry.operationId && (
                      <>
                        <span className="modal-field-label">Operation ID</span>
                        <span className="modal-field-value">{selectedEntry.operationId}</span>
                      </>
                    )}
                    {selectedEntry.operationName && (
                      <>
                        <span className="modal-field-label">Operation Name</span>
                        <span className="modal-field-value">{selectedEntry.operationName}</span>
                      </>
                    )}
                    {selectedEntry.operationParentId && (
                      <>
                        <span className="modal-field-label">Parent ID</span>
                        <span className="modal-field-value">{selectedEntry.operationParentId}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {(selectedEntry.cloudRoleName || selectedEntry.cloudRoleInstance) && (
                <div className="modal-section">
                  <div className="modal-section-title">Infrastructure</div>
                  <div className="modal-fields">
                    {selectedEntry.cloudRoleName && (
                      <>
                        <span className="modal-field-label">Cloud Role</span>
                        <span className="modal-field-value">{selectedEntry.cloudRoleName}</span>
                      </>
                    )}
                    {selectedEntry.cloudRoleInstance && (
                      <>
                        <span className="modal-field-label">Role Instance</span>
                        <span className="modal-field-value">{selectedEntry.cloudRoleInstance}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {(selectedEntry.sessionId || selectedEntry.userAuthenticatedId || selectedEntry.userId) && (
                <div className="modal-section">
                  <div className="modal-section-title">User & Session</div>
                  <div className="modal-fields">
                    {selectedEntry.sessionId && (
                      <>
                        <span className="modal-field-label">Session ID</span>
                        <span className="modal-field-value">{selectedEntry.sessionId}</span>
                      </>
                    )}
                    {selectedEntry.userAuthenticatedId && (
                      <>
                        <span className="modal-field-label">Authenticated User</span>
                        <span className="modal-field-value">{selectedEntry.userAuthenticatedId}</span>
                      </>
                    )}
                    {selectedEntry.userId && (
                      <>
                        <span className="modal-field-label">User ID</span>
                        <span className="modal-field-value">{selectedEntry.userId}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {(selectedEntry.clientIp || selectedEntry.clientBrowser || selectedEntry.clientCity || selectedEntry.clientCountryOrRegion) && (
                <div className="modal-section">
                  <div className="modal-section-title">Client</div>
                  <div className="modal-fields">
                    {selectedEntry.clientIp && (
                      <>
                        <span className="modal-field-label">IP Address</span>
                        <span className="modal-field-value">{selectedEntry.clientIp}</span>
                      </>
                    )}
                    {selectedEntry.clientBrowser && (
                      <>
                        <span className="modal-field-label">Browser</span>
                        <span className="modal-field-value">{selectedEntry.clientBrowser}</span>
                      </>
                    )}
                    {selectedEntry.clientCity && (
                      <>
                        <span className="modal-field-label">City</span>
                        <span className="modal-field-value">{selectedEntry.clientCity}</span>
                      </>
                    )}
                    {selectedEntry.clientCountryOrRegion && (
                      <>
                        <span className="modal-field-label">Country / Region</span>
                        <span className="modal-field-value">{selectedEntry.clientCountryOrRegion}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppInsights;
